import type { EvergladesProductsApiQueryParams, SearchContext } from '@shared/types/types.ts';
import { composeSponsoredBlackFridaySlider } from '@tilelist/domain/black-friday-slider/BlackFridaySliderComposition.ts';
import { isProductItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { BlackFridaySliderItem, ProductItem, TileListItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';
import { DEFAULT_ONEX } from '@tilelist/domain/experiments/OnExAdapter.ts';
import { findInsertIndexAfterNthProduct } from '@tilelist/domain/feature-visibility/SDABillboard.ts';

export const BLACK_FRIDAY_SLIDER_POSITION = 12;
export const BLACK_FRIDAY_BANNER_POSITION = 18;
export const BLACK_FRIDAY_SLIDER_MIN_PRODUCTS = 5;
export const COLLAPSED_BLACK_FRIDAY_SLIDER: BlackFridaySliderItem = {
    type: 'BlackFridaySlider',
    state: 'collapsed',
};

const ELIGIBLE_ASSORTMENTS = new Set(['unterhaltungselektronik', 'haushaltsgeräte', 'reinigungsgeräte']);

export function isBlackFridaySliderEligible(searchContext?: SearchContext): boolean {
    const assortment = searchContext?.assortment?.toLowerCase();
    return !!assortment && ELIGIBLE_ASSORTMENTS.has(assortment);
}

/**
 *
 *
 *
 */
export function insertBlackFridaySliderSlot(
    tileListItems: TileListItem[],
    searchContext: SearchContext | undefined,
    currentPage: number,
    active: boolean,
    blackFridayRule?: string,
): TileListItem[] {
    if (!active) return tileListItems;
    if (currentPage !== 1 || !isBlackFridaySliderEligible(searchContext)) return tileListItems;

    const insertIndex = findInsertIndexAfterNthProduct(tileListItems, BLACK_FRIDAY_SLIDER_POSITION);
    if (insertIndex >= tileListItems.length && countProducts(tileListItems) < BLACK_FRIDAY_SLIDER_POSITION) {
        return tileListItems;
    }

    const slot: BlackFridaySliderItem = {
        type: 'BlackFridaySlider',
        state: 'pending',
        ...(blackFridayRule ? { rule: blackFridayRule } : {}),
    };
    const hasBanner = tileListItems[insertIndex]?.type === 'PCB';
    const withoutBanner = hasBanner
        ? [...tileListItems.slice(0, insertIndex), ...tileListItems.slice(insertIndex + 1)]
        : tileListItems;
    const withSlot = [...withoutBanner.slice(0, insertIndex), slot, ...withoutBanner.slice(insertIndex)];
    if (!hasBanner) return withSlot;

    const bannerIndex = findInsertIndexAfterNthProduct(withSlot, BLACK_FRIDAY_BANNER_POSITION);
    return [...withSlot.slice(0, bannerIndex), { type: 'PCB' as const }, ...withSlot.slice(bannerIndex)];
}

function countProducts(items: TileListItem[]): number {
    return items.filter(isProductItem).length;
}

export const BLACK_FRIDAY_SLIDER_MAX_PRODUCTS = 20;
const BLACK_FRIDAY_SORT_ORDER = 'bewertung';

/*                                                                                                           */
export function isBlackFridayRuleRequested(onex: OnEx, firstPage: boolean): boolean {
    return firstPage && onex.isToggleActive('REPTILE_1717_BLACK_FRIDAY_SLIDER');
}

/**
 *
 *
 */
export function toBlackFridayProductsQuery(
    blackFridayRule: string,
    onex: OnEx = DEFAULT_ONEX,
): EvergladesProductsApiQueryParams {
    return {
        rule: blackFridayRule,
        intents: isSponsoredBlackFridayActive(onex) ? ['ranked', 'sponsored'] : ['ranked'],
        'ranked.offset': 0,
        'ranked.limit': BLACK_FRIDAY_SLIDER_MAX_PRODUCTS,
        'ranked.sortOrder': BLACK_FRIDAY_SORT_ORDER,
    };
}

export function isSponsoredBlackFridayActive(onex: OnEx = DEFAULT_ONEX): boolean {
    return onex.isExperimentGroup('SPONSORED_BLACK_FRIDAY_PRODUCTS', 'E3958B');
}

/*                                                                                             */
export function toOrganicBlackFridaySlider(enrichedItems: ProductItem[]): BlackFridaySliderItem {
    const items = enrichedItems.filter((item) => item.variations.length > 0 && !!item.currentVariationId);
    if (items.length < BLACK_FRIDAY_SLIDER_MIN_PRODUCTS) return COLLAPSED_BLACK_FRIDAY_SLIDER;
    return { type: 'BlackFridaySlider', state: 'ready', items };
}

/*                                                                                                             */
export function toSponsoredBlackFridaySlider(organic: ProductItem[], sponsored: ProductItem[]): BlackFridaySliderItem {
    const items = composeSponsoredBlackFridaySlider({ organic, sponsored });
    if (!items) return COLLAPSED_BLACK_FRIDAY_SLIDER;
    return { type: 'BlackFridaySlider', state: 'ready', items };
}
